import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Divider,
    Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    TrendingUp,
    LocalShipping,
    Business,
    MonetizationOn,
    Warning,
    CheckCircle,
    Scale,
    Assignment,
    Refresh,
    Timeline,
} from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { getFreightRecords, FreightRecord } from '../api/handleFreightRecords';
import { getAllCompanies, Company } from '../api/handleCompanies';
import { useAccount } from '../contexts/AccountContext';

interface DashboardStats {
    totalRevenue: number;
    totalFreightCosts: number;
    totalTonnage: number;
    totalRecords: number;
    recordsNeedingReview: number;
    paidRecords: number;
    unpaidRecords: number;
    averageConfidenceScore: number;
    activeCarriers: number;
    activeSuppliers: number;
    activeOutlets: number;
}

interface MonthlyData {
    month: string;
    revenue: number;
    tonnage: number;
    recordCount: number;
}

interface TopCompany {
    id: string;
    name: string;
    revenue: number;
    tonnage: number;
    recordCount: number;
}

const Dashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Data states
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [records, setRecords] = useState<FreightRecord[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
    const [topCarriers, setTopCarriers] = useState<TopCompany[]>([]);
    const [topSuppliers, setTopSuppliers] = useState<TopCompany[]>([]);
    const [recentRecords, setRecentRecords] = useState<FreightRecord[]>([]);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

    const fetchData = async () => {
        if (!currentAccount) return;

        try {
            setLoading(true);
            setError(null);

            const impersonationData = isImpersonating && impersonatedUser ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            // Fetch all data in parallel
            const [recordsData, companiesData] = await Promise.all([
                getFreightRecords(impersonationData),
                getAllCompanies(impersonationData),
            ]);

            setRecords(recordsData);
            setCompanies(companiesData);

            // Process data for dashboard
            processStatistics(recordsData, companiesData);
            processMonthlyData(recordsData);
            processTopCompanies(recordsData, companiesData);
            setRecentRecords(recordsData.slice(0, 5)); // Latest 5 records
            setLastUpdated(new Date());

        } catch (err: any) {
            setError(err.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const processStatistics = (records: FreightRecord[], companies: Company[]) => {
        const stats: DashboardStats = {
            totalRevenue: records.reduce((sum, r) => sum + (r.grossPrice || 0), 0),
            totalFreightCosts: records.reduce((sum, r) => sum + (r.freightCost || 0), 0),
            totalTonnage: records.reduce((sum, r) => sum + (r.netTons || 0), 0),
            totalRecords: records.length,
            recordsNeedingReview: records.filter(r => r.needsReview).length,
            paidRecords: records.filter(r => r.paidStatus).length,
            unpaidRecords: records.filter(r => !r.paidStatus).length,
            averageConfidenceScore: records.length > 0
                ? records.reduce((sum, r) => sum + (r.confidenceScore || 0), 0) / records.length
                : 0,
            activeCarriers: companies.filter(c => c.isCarrier).length,
            activeSuppliers: companies.filter(c => c.isSupplier).length,
            activeOutlets: companies.filter(c => c.isOutlet).length,
        };

        setStats(stats);
    };

    const processMonthlyData = (records: FreightRecord[]) => {
        const monthlyMap = new Map<string, MonthlyData>();

        records.forEach(record => {
            const date = record.deliveryDate ? new Date(record.deliveryDate) : new Date(record.createdAt || '');
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });

            if (!monthlyMap.has(monthKey)) {
                monthlyMap.set(monthKey, {
                    month: monthLabel,
                    revenue: 0,
                    tonnage: 0,
                    recordCount: 0,
                });
            }

            const monthData = monthlyMap.get(monthKey)!;
            monthData.revenue += record.grossPrice || 0;
            monthData.tonnage += record.netTons || 0;
            monthData.recordCount += 1;
        });

        const sortedData = Array.from(monthlyMap.values())
            .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime())
            .slice(-6); // Last 6 months

        setMonthlyData(sortedData);
    };

    const processTopCompanies = (records: FreightRecord[], companies: Company[]) => {
        const carrierMap = new Map<string, TopCompany>();
        const supplierMap = new Map<string, TopCompany>();

        records.forEach(record => {
            // Process carriers
            if (record.carrier) {
                const carrier = companies.find(c => c.name === record.carrier && c.isCarrier);
                if (carrier) {
                    if (!carrierMap.has(carrier.id!)) {
                        carrierMap.set(carrier.id!, {
                            id: carrier.id!,
                            name: carrier.name,
                            revenue: 0,
                            tonnage: 0,
                            recordCount: 0,
                        });
                    }
                    const carrierData = carrierMap.get(carrier.id!)!;
                    carrierData.revenue += record.grossPrice || 0;
                    carrierData.tonnage += record.netTons || 0;
                    carrierData.recordCount += 1;
                }
            }

            // Process suppliers
            if (record.supplier) {
                const supplier = companies.find(c => c.name === record.supplier && c.isSupplier);
                if (supplier) {
                    if (!supplierMap.has(supplier.id!)) {
                        supplierMap.set(supplier.id!, {
                            id: supplier.id!,
                            name: supplier.name,
                            revenue: 0,
                            tonnage: 0,
                            recordCount: 0,
                        });
                    }
                    const supplierData = supplierMap.get(supplier.id!)!;
                    supplierData.revenue += record.grossPrice || 0;
                    supplierData.tonnage += record.netTons || 0;
                    supplierData.recordCount += 1;
                }
            }
        });

        setTopCarriers(Array.from(carrierMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5));
        setTopSuppliers(Array.from(supplierMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 5));
    };

    useEffect(() => {
        fetchData();
    }, [currentAccount, isImpersonating, impersonatedUser]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatTonnage = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(value);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error" action={
                    <IconButton color="inherit" size="small" onClick={fetchData}>
                        <Refresh />
                    </IconButton>
                }>
                    {error}
                </Alert>
            </Container>
        );
    }

    const paymentStatusData = [
        { id: 0, value: stats?.paidRecords || 0, label: 'Paid', color: '#4caf50' },
        { id: 1, value: stats?.unpaidRecords || 0, label: 'Unpaid', color: '#ff9800' },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Dashboard
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">
                        Last updated: {lastUpdated.toLocaleTimeString()}
                    </Typography>
                    <IconButton onClick={fetchData} size="small">
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {/* Key Metrics Cards */}
            <Grid container spacing={3} mb={4}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, color: 'white' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">
                                        {formatCurrency(stats?.totalRevenue || 0)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Revenue
                                    </Typography>
                                </Box>
                                <MonetizationOn sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`, color: 'white' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">
                                        {formatTonnage(stats?.totalTonnage || 0)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Tons
                                    </Typography>
                                </Box>
                                <Scale sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`, color: 'white' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">
                                        {stats?.totalRecords || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Total Records
                                    </Typography>
                                </Box>
                                <Assignment sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card sx={{ background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`, color: 'white' }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography variant="h4" fontWeight="bold">
                                        {stats?.recordsNeedingReview || 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Need Review
                                    </Typography>
                                </Box>
                                <Warning sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3} mb={4}>
                {/* Monthly Revenue Trend */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Monthly Revenue & Tonnage Trends
                            </Typography>
                            {monthlyData.length > 0 ? (
                                <Box sx={{ width: '100%', height: 300 }}>
                                    <LineChart
                                        xAxis={[{
                                            data: monthlyData.map(d => d.month),
                                            scaleType: 'point'
                                        }]}
                                        series={[
                                            {
                                                data: monthlyData.map(d => d.revenue),
                                                label: 'Revenue ($)',
                                                color: theme.palette.primary.main,
                                            },
                                            {
                                                data: monthlyData.map(d => d.tonnage * 1000), // Scale tonnage for visibility
                                                label: 'Tonnage (x1000)',
                                                color: theme.palette.success.main,
                                            },
                                        ]}
                                        height={280}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="text.secondary">No data available</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Payment Status */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Payment Status
                            </Typography>
                            {paymentStatusData.some(d => d.value > 0) ? (
                                <Box sx={{ height: 300, display: 'flex', alignItems: 'center' }}>
                                    <PieChart
                                        series={[
                                            {
                                                data: [
                                                    { id: 0, value: stats?.paidRecords || 0, label: 'Paid', color: '#4caf50' },
                                                    { id: 1, value: stats?.unpaidRecords || 0, label: 'Unpaid', color: '#ff9800' },
                                                ],
                                            },
                                        ]}
                                        height={280}
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography color="text.secondary">No payment data available</Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Bottom Row */}
            <Grid container spacing={3}>
                {/* Company Stats */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Active Companies
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <LocalShipping color="primary" />
                                        <Typography>Carriers</Typography>
                                    </Box>
                                    <Chip label={stats?.activeCarriers || 0} color="primary" />
                                </Box>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Business color="success" />
                                        <Typography>Suppliers</Typography>
                                    </Box>
                                    <Chip label={stats?.activeSuppliers || 0} color="success" />
                                </Box>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Business color="info" />
                                        <Typography>Outlets</Typography>
                                    </Box>
                                    <Chip label={stats?.activeOutlets || 0} color="info" />
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="body2">Avg. AI Confidence</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {((stats?.averageConfidenceScore || 0) * 100).toFixed(1)}%
                                    </Typography>
                                    {(stats?.averageConfidenceScore || 0) > 0.8 ? (
                                        <CheckCircle color="success" fontSize="small" />
                                    ) : (
                                        <Warning color="warning" fontSize="small" />
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Top Performers */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Top Carriers by Revenue
                            </Typography>
                            <List dense>
                                {topCarriers.length > 0 ? topCarriers.map((carrier, index) => (
                                    <ListItem key={carrier.id} sx={{ px: 0 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{
                                                bgcolor: theme.palette.primary.main,
                                                width: 36,
                                                height: 36,
                                                fontSize: '0.875rem',
                                                fontWeight: 600
                                            }}>
                                                {index + 1}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={carrier.name}
                                            secondary={`${formatCurrency(carrier.revenue)} • ${formatTonnage(carrier.tonnage)} tons`}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                    </ListItem>
                                )) : (
                                    <Typography color="text.secondary" variant="body2">
                                        No carrier data available
                                    </Typography>
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Recent Activity */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Recent Records
                            </Typography>
                            <List dense>
                                {recentRecords.length > 0 ? recentRecords.map((record) => (
                                    <ListItem key={record.id} sx={{ px: 0 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{
                                                bgcolor: record.needsReview ? theme.palette.warning.main : theme.palette.success.main,
                                                width: 36,
                                                height: 36
                                            }}>
                                                {record.needsReview ?
                                                    <Warning sx={{ fontSize: 20 }} /> :
                                                    <CheckCircle sx={{ fontSize: 20 }} />
                                                }
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={record.ticketNumber || 'No ticket #'}
                                            secondary={`${record.supplier || 'Unknown'} • ${formatCurrency(record.grossPrice || 0)}`}
                                            primaryTypographyProps={{ variant: 'body2' }}
                                            secondaryTypographyProps={{ variant: 'caption' }}
                                        />
                                    </ListItem>
                                )) : (
                                    <Typography color="text.secondary" variant="body2">
                                        No recent records
                                    </Typography>
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Dashboard;
